/** Helpers for sidebar layout drag-and-drop state. */

export function cloneLayouts(layouts) {
  return JSON.parse(JSON.stringify(layouts || {}));
}

export function parseDragPayload(event) {
  try {
    return JSON.parse(event.dataTransfer.getData('application/json') || '{}');
  } catch {
    return null;
  }
}

/**
 * @param {object} layouts - keyed by role id string
 * @param {number|string} roleId
 * @param {number[]} groupPath - [] for root, [groupIndex] for inside group
 */
export function getContainerList(layouts, roleId, groupPath = []) {
  const key = String(roleId);
  const layout = layouts[key] || [];
  if (!groupPath.length) return layout;
  const groupIndex = groupPath[0];
  const node = layout[groupIndex];
  if (node?.kind !== 'group') return [];
  return node.children || [];
}

export function setContainerList(layouts, roleId, groupPath, list) {
  const key = String(roleId);
  const next = cloneLayouts(layouts);
  if (!groupPath.length) {
    next[key] = list;
    return next;
  }
  const layout = [...(next[key] || [])];
  const groupIndex = groupPath[0];
  const groupNode = { ...layout[groupIndex], children: list };
  layout[groupIndex] = groupNode;
  next[key] = layout;
  return next;
}

export function removeFromContainer(layouts, roleId, groupPath, index) {
  const list = [...getContainerList(layouts, roleId, groupPath)];
  const [removed] = list.splice(index, 1);
  return { layouts: setContainerList(layouts, roleId, groupPath, list), removed };
}

/** Remove a dropdown group node (and all nested links) at the given index. */
export function removeGroupAt(layouts, roleId, groupPath, index) {
  const list = getContainerList(layouts, roleId, groupPath);
  const node = list[index];
  if (node?.kind !== 'group') return layouts;
  return removeFromContainer(layouts, roleId, groupPath, index).layouts;
}

/** Remove a single link from a role layout (does not delete routes or other roles). */
export function removeNavLinkAt(layouts, roleId, groupPath, index) {
  const list = getContainerList(layouts, roleId, groupPath);
  const node = list[index];
  if (node?.kind !== 'link') return layouts;
  return removeFromContainer(layouts, roleId, groupPath, index).layouts;
}

export function insertIntoContainer(layouts, roleId, groupPath, index, node) {
  const list = [...getContainerList(layouts, roleId, groupPath)];
  list.splice(index, 0, node);
  return setContainerList(layouts, roleId, groupPath, list);
}

export function moveSidebarCard(layouts, drag, drop) {
  if (!drag?.node) return layouts;
  let next = cloneLayouts(layouts);

  const srcRole = String(drag.roleId);
  const dstRole = String(drop.roleId);
  const srcPath = drag.groupPath || [];
  const dstPath = drop.groupPath || [];

  const { layouts: afterRemove, removed } = removeFromContainer(
    next,
    srcRole,
    srcPath,
    drag.index
  );
  if (!removed) return layouts;
  next = afterRemove;

  let insertIndex = drop.index;
  if (srcRole === dstRole && srcPath.join() === dstPath.join() && drag.index < drop.index) {
    insertIndex -= 1;
  }

  return insertIntoContainer(next, dstRole, dstPath, insertIndex, removed);
}

/** Apply the same layout to School Head and School Admin columns. */
export function syncSchoolSidebarLayouts(layouts, layout) {
  const next = cloneLayouts(layouts);
  const copy = JSON.parse(JSON.stringify(layout));
  next['1'] = copy;
  next['2'] = JSON.parse(JSON.stringify(layout));
  return next;
}

export function appendNavLinksToRole(layouts, roleId, navIds) {
  const key = String(roleId);
  const existing = new Set(collectNavIdsInLayout(layouts[key] || []));
  const toAdd = (navIds || []).filter((id) => id && !existing.has(id));
  if (!toAdd.length) return layouts;

  let next = cloneLayouts(layouts);
  const list = [...getContainerList(next, roleId, [])];
  toAdd.forEach((navId) => {
    list.push({ kind: 'link', navId });
  });
  return setContainerList(next, roleId, [], list);
}

/** How many nav ids were newly appended (not already on the role layout). */
export function countNewNavLinksForRole(layouts, roleId, navIds) {
  const key = String(roleId);
  const existing = new Set(collectNavIdsInLayout(layouts[key] || []));
  return (navIds || []).filter((id) => id && !existing.has(id)).length;
}

export function collectNavIdsInLayout(layout) {
  const ids = [];
  const walk = (nodes) => {
    (nodes || []).forEach((n) => {
      if (n.kind === 'link') ids.push(n.navId);
      if (n.kind === 'group') walk(n.children);
    });
  };
  walk(layout);
  return ids;
}

export function getNavLabelForRole(roleId, navId, catalogItem) {
  const overrides = {
    8: { item107: 'HR Payroll' },
  };
  const role = Number(roleId);
  if (overrides[role]?.[navId]) return overrides[role][navId];
  return catalogItem?.label || navId;
}

export function getNavPathForRole(roleId, navId, catalogItem) {
  const overrides = {
    8: { item107: '/super-admin/hr-payroll' },
  };
  const role = Number(roleId);
  if (overrides[role]?.[navId]) {
    const path = overrides[role][navId];
    return path;
  }
  if (!catalogItem) return '/';
  return `${catalogItem.path || '/'}${catalogItem.search || ''}`;
}
